import {
  Component,
  inject,
  signal,
  OnInit,
  PLATFORM_ID,
  Inject,
} from '@angular/core';
import { CommonModule, isPlatformBrowser } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Router, RouterModule } from '@angular/router';
import { Title, Meta } from '@angular/platform-browser';
import { SelectButtonModule } from 'primeng/selectbutton';
import { CheckoutSummaryComponent } from '../../components/cart/checkout-summary/checkout-summary';
import { ProductCardsComponent } from '../../components/shared/product-cards/product-cards.component';
import {
  LucideAngularModule,
  Trash2Icon,
  PlusIcon,
  MinusIcon,
  ShoppingCartIcon,
  EditIcon,
  CheckIcon,
  XIcon,
  ArrowLeftIcon,
  DownloadIcon,
  PhoneIcon,
  MessageCircleIcon,
  ExternalLinkIcon,
  CarIcon,
  SparklesIcon,
  ZapIcon,
  StarIcon,
  HeartIcon,
  BookmarkIcon,
  TargetIcon,
  RocketIcon,
  UserIcon,
  MapPinIcon,
} from 'lucide-angular';
import { MainLayout } from '../../layouts/main-layout';
import { CartService } from '../../services/cart.service';
import { StoreService } from '../../services/store.service';
import { EmailValidationService } from '../../services/email-validation.service';
import { ProductTrackingService } from '../../services/product-tracking.service';
import { ProductService } from '../../services/product.service';
import { smartTitle } from '../../utils/pipes/smart-capitalize';
import { Cart, CartItem } from '../../types/Cart.types';
import { HttpClient } from '@angular/common/http';
import { VehicleService } from '../../services/vehicle.service';
import { User } from '../../types/auth.types';
import { firstValueFrom } from 'rxjs';
import { MarginWrapper } from '../../layouts/margin-wrapper';
import { NavigationService } from '../../services/navigation.service';
import { Producto } from '../../types/product.types';
import { LOCAL_STORAGE_SAVED_FOR_LATER } from '../../config/variables';

// Enum para los estados del carrito
export enum CartStatus {
  ABIERTO = 1,
  COTIZADO = 2,
  FACTURADO = 3,
}

// Enum para las operaciones de cantidad
export enum QuantityOperation {
  INCREMENTAR = 1,
  DECREMENTAR = 2,
}

@Component({
  selector: 'allas-carrito',

  imports: [
    MainLayout,
    MarginWrapper,
    CommonModule,
    FormsModule,
    RouterModule,
    LucideAngularModule,
    CheckoutSummaryComponent,
    ProductCardsComponent,
    SelectButtonModule,
  ],
  templateUrl: './carrito.component.html',
})
export class Carrito implements OnInit {
  private title = inject(Title);
  private meta = inject(Meta);
  private router = inject(Router);
  private navigationService = inject(NavigationService);
  private productTrackingService = inject(ProductTrackingService);
  private productService = inject(ProductService);
  private storeService = inject(StoreService);
  private vehicleService = inject(VehicleService);
  private platformId = inject(PLATFORM_ID);

  carrito = signal<Cart | null>(null);

  cartService = inject(CartService);
  emailValidationService = inject(EmailValidationService);
  usuarioDigital = signal<User | null>(
    this.emailValidationService.getCurrentUser()
  );

  // Productos vistos recientemente para recomendaciones
  productosVistosRecientemente: Producto[] = [];
  cantidadProductosAMostrar = 6;

  // Items guardados para más tarde
  savedForLaterItems = signal<CartItem[]>([]);

  // Opciones de entrega
  deliveryOptions = [
    {
      label: 'Recoger en tienda',
      value: 'pickup',
      description: 'Listo hoy',
      price: 'GRATIS',
    },
    {
      label: 'Envío a Casa',
      value: 'home',
      description: 'Entregado mañana',
      price: 'GRATIS mayor de L.1,000',
    },
    {
      label: 'Entrega el mismo día',
      value: 'same-day',
      description: 'Consiguelo hoy',
      price: 'A partir de L.500',
    },
  ];

  ngOnInit(): void {
    this.setMetaTags();

    // Cargar productos vistos recientemente para recomendaciones
    this.getProductosVistosRecientemente();

    // Obtener y setear el carrito
    this.getAndSetCart();

    // Cargar items guardados para más tarde
    this.loadSavedForLaterItems();
  }

  private setMetaTags() {
    const pageTitle = 'Mi Carrito | Allas Repuestos';
    const description =
      'Revisa tu carrito de compras en Allas Repuestos. Agrega más productos, ajusta cantidades y finaliza tu compra con envío a domicilio en Honduras.';
    const imageUrl = 'https://www.allasrepuestos.com/logos/isotipo.webp';

    this.title.setTitle(pageTitle);

    this.meta.updateTag({
      name: 'description',
      content: description,
    });

    this.meta.updateTag({
      property: 'og:title',
      content: pageTitle,
    });

    this.meta.updateTag({
      property: 'og:description',
      content: description,
    });

    this.meta.updateTag({
      property: 'og:type',
      content: 'website',
    });

    this.meta.updateTag({
      property: 'og:image',
      content: imageUrl,
    });

    this.meta.updateTag({
      name: 'twitter:card',
      content: 'summary_large_image',
    });

    this.meta.updateTag({
      name: 'twitter:title',
      content: pageTitle,
    });

    this.meta.updateTag({
      name: 'twitter:description',
      content: description,
    });
  }

  getAndSetCart(): void {
    this.cartService.cart$.subscribe((cart) => {
      this.carrito.set(cart);
    });
  }

  // Icons
  readonly shoppingCartIcon = ShoppingCartIcon;
  readonly trashIcon = Trash2Icon;
  readonly plusIcon = PlusIcon;
  readonly minusIcon = MinusIcon;
  readonly editIcon = EditIcon;
  readonly checkIcon = CheckIcon;
  readonly cancelIcon = XIcon;
  readonly arrowLeftIcon = ArrowLeftIcon;
  readonly downloadIcon = DownloadIcon;
  readonly phoneIcon = PhoneIcon;
  readonly messageCircleIcon = MessageCircleIcon;
  readonly externalIcon = ExternalLinkIcon;
  readonly carIcon = CarIcon;
  readonly sparklesIcon = SparklesIcon;
  readonly zapIcon = ZapIcon;
  readonly starIcon = StarIcon;
  readonly heartIcon = HeartIcon;
  readonly bookmarkIcon = BookmarkIcon;
  readonly targetIcon = TargetIcon;
  readonly rocketIcon = RocketIcon;
  readonly userIcon = UserIcon;
  readonly mapPinIcon = MapPinIcon;

  // Estado para edición de notas
  editingNotes = signal<number | null>(null);
  tempNotes = signal<string>('');

  // Información del usuario
  userName = '';

  smartTitle = smartTitle;

  // Método para verificar si hay vehículo por defecto
  hasDefaultVehicle(): boolean {
    return !!this.vehicleService.getDefaultVehicle();
  }

  // Método para verificar si hay tienda por defecto
  hasDefaultStore(): boolean {
    return !!this.storeService.getSelectedStore();
  }

  // Método para validar información de contacto
  isContactInfoValid(): boolean {
    const usuarioDigital = this.usuarioDigital();
    if (usuarioDigital) return true;

    return this.userName.trim().length > 0;
  }

  goBack(): void {
    this.navigationService.goBack();
  }

  // Método para ir al garage
  goToGarage(): void {
    this.router.navigate(['/garage']);
  }

  // Método para ir a la selección de tiendas
  goToStoreSelection(): void {
    this.router.navigate(['/mi-tienda']);
  }

  // Método para ir a la página de búsqueda de tiendas
  goToFindStore(): void {
    this.router.navigate(['/encuentra-tu-tienda']);
  }

  // Método para agregar vehículo rápido
  addQuickVehicle(): void {
    this.router.navigate(['/garage/agregar-vehiculo']);
  }

  async increaseQuantity(item: CartItem): Promise<void> {
    const currentQuantity = item.cantidad + 1;
    this.cartService.updateItemInCart({
      ...item,
      cantidad: currentQuantity,
    });
  }

  async decreaseQuantity(item: CartItem): Promise<void> {
    const currentQuantity = item.cantidad - 1;

    if (currentQuantity >= 1) {
      this.cartService.updateItemInCart({
        ...item,
        cantidad: currentQuantity,
      });
    }
  }
  removeItem(item: CartItem): void {
    this.cartService.removeItemFromCart(item.id);
  }

  clearCart(): void {
    if (confirm('¿Limpiar carrito?')) {
      this.cartService.clearCart();
    }
  }

  startEditNotes(item: CartItem, currentNotes: string): void {
    this.editingNotes.set(item.id);
    this.tempNotes.set(currentNotes);
  }

  updateTempNotes(event: any): void {
    this.tempNotes.set(event.target.value);
  }

  saveNotes(item: CartItem): void {
    this.cartService.updateItemInCart({
      ...item,
      notas: this.tempNotes(),
    });
    this.editingNotes.set(null);
    this.tempNotes.set('');
  }

  cancelEditNotes(): void {
    this.editingNotes.set(null);
    this.tempNotes.set('');
  }

  /**
   * Cambia el estado del carrito actual a "Cotizado"
   */
  private async updateCartStatusToQuoted(): Promise<void> {
    // Obtener el ID del carrito del servicio
    const cartId = this.cartService.getCurrentCartId();

    const nombreContacto = this.usuarioDigital()?.nombrePreferido || '';
    const telefonoContacto = this.usuarioDigital()?.telefono?.telefono || '';

    if (cartId) {
      await this.cartService.updateCartStatus(
        cartId.toString(),
        CartStatus.COTIZADO,
        nombreContacto,
        telefonoContacto
      );
    } else {
      console.warn('No se encontró ID del carrito para actualizar el estado');
    }
  }

  async requestWhatsAppQuote(username: string): Promise<void> {
    console.log('requestWhatsAppQuote', username);
    // Cambiar el estado del carrito a "Cotizado" antes de abrir WhatsApp
    await this.updateCartStatusToQuoted();

    const items = this.cartService.cartItems();
    const vehicle = this.vehicleService.getDefaultVehicle();
    const store = this.storeService.getSelectedStore();

    let message = '¡Hola! Me gustaría cotizar los siguientes repuestos:\n\n';

    // Información del cliente
    message += `👤 *INFORMACIÓN DEL CLIENTE:*\n`;
    message += `Nombre: ${
      this.usuarioDigital()?.nombrePreferido || username
    }\n\n`;

    // Información del vehículo
    if (vehicle) {
      message += `🚗 *VEHÍCULO:*\n`;
      message += `${vehicle.anio} ${vehicle.marca} ${vehicle.modelo}\n`;
      if (vehicle.motor) {
        message += `Motor: ${vehicle.motor}\n`;
      }
      if (vehicle.vin) {
        message += `VIN: ${vehicle.vin}\n`;
      }
      if (
        vehicle.alias &&
        vehicle.alias !== `${vehicle.anio} ${vehicle.marca} ${vehicle.modelo}`
      ) {
        message += `Apodo: ${vehicle.alias}\n`;
      }
      message += '\n';
    }

    // Información de la tienda
    if (store) {
      message += `📍 *TIENDA SELECCIONADA:*\n`;
      message += `${store.name}\n`;
      message += `${store.address}\n`;
      message += `${store.city}\n`;
      message += `Teléfono: ${store.phone}\n`;
      message += '\n';
    }

    // Lista de productos
    message += `🛒 *REPUESTOS SOLICITADOS:*\n`;
    items.forEach((item, index) => {
      message += `${index + 1}. ${smartTitle(item.grupo?.name || '')}`;

      // Agregar información específica del producto si está disponible
      if (item.producto?.sku || item.producto?.part_number) {
        message += `\n   🔢 `;
        if (item.producto?.part_number) {
          message += `Parte: ${item.producto?.part_number}`;
        }
        if (item.producto?.sku) {
          message += `${item.producto?.part_number ? ' | ' : ''}Código: ${
            item.producto?.sku
          }`;
        }
        if (item.selectedDelivery) {
          message += `\n   🏪 Método de entrega	: ${item.selectedDelivery}`;
        }
      }

      // Precio unitario
      if (item.producto?.precio) {
        message += `\n   💰 Precio referencia: L. ${item.producto?.precio}`;
      }

      // Atributos del producto
      if (item.producto?.atributos) {
        const attributes: string[] = [];

        item.producto.atributos
          .filter((a) => a.filterable && !a.is_from_api_catalogo)
          .forEach((attribute) => {
            attributes.push(`${attribute.label}: ${attribute.value}`);
          });
        if (attributes.length > 0) {
          message += `\n   📋 Especificaciones: ${attributes.join(' | ')}`;
        }
      }

      // Cantidad
      if (item.cantidad > 1) {
        message += `\n   📦 Cantidad: ${item.cantidad} unidades`;
      }

      // Notas del usuario
      if (item.notas) {
        message += `\n   📝 Nota: ${item.notas}`;
      }
      message += '\n';
    });

    message +=
      '\n¿Podrían enviarme una cotización detallada con precios y disponibilidad?\n';
    message += '\n¡Muchas gracias! 🙏';

    const encodedMessage = encodeURIComponent(message);
    const whatsappUrl = `https://wa.me/50495089953?text=${encodedMessage}`;
    if (isPlatformBrowser(this.platformId)) {
      window.open(whatsappUrl, '_blank');
    }
  }

  requestPhoneQuote(): void {
    if (isPlatformBrowser(this.platformId)) {
      window.location.href = 'tel:+50422834540';
    }
  }

  /**
   * Obtiene los productos vistos recientemente para mostrar como recomendaciones
   */
  async getProductosVistosRecientemente() {
    const mostViewed = this.productTrackingService.getMostViewedProducts(
      this.cantidadProductosAMostrar
    );

    if (!mostViewed.length) {
      return;
    }

    try {
      this.productosVistosRecientemente =
        (await firstValueFrom(
          this.productService.getProductsBySkus(
            mostViewed.map((viewLog) => viewLog.productId)
          )
        )) || [];
    } catch (error) {
      console.error('Error al cargar productos vistos recientemente:', error);
    }
  }

  /**
   * Maneja el evento de agregar producto al carrito desde las recomendaciones
   * @param producto - Producto a agregar al carrito
   */
  onAddToCartFromRecommendations(producto: Producto): void {
    console.log('Producto recomendado agregado al carrito:', producto);
    this.cartService.addItemToCart({
      cantidad: 1,
      producto: producto,
    });
  }

  /**
   * Formatea el precio con la parte entera grande y los decimales en superíndice
   * @param price - Precio a formatear
   * @returns String HTML con el precio formateado
   */
  formatPrice(price: number): string {
    const priceStr = price.toFixed(2);
    const [integerPart, decimalPart] = priceStr.split('.');

    return `${integerPart}<sup class="text-xs font-normal">${decimalPart}</sup>`;
  }

  /**
   * Selecciona una opción de entrega para un item del carrito
   * @param item - Item del carrito
   * @param deliveryValue - Valor de la opción de entrega seleccionada
   */
  selectDeliveryOption(item: any, deliveryValue: string): void {
    item.selectedDelivery = deliveryValue;
  }

  /**
   * Retorna las clases CSS para los botones de entrega según su estado
   * @param isSelected - Si el botón está seleccionado
   * @returns String con las clases CSS
   */
  getDeliveryButtonClasses(isSelected: boolean): string {
    if (isSelected) {
      return 'border-red-600 bg-red-50 relative';
    } else {
      return 'border-gray-300 bg-white relative';
    }
  }

  /**
   * Formatea la descripción para resaltar solo las palabras específicas en azul
   * @param description - Descripción a formatear
   * @returns String HTML con las palabras resaltadas
   */
  getFormattedDescription(description: string): string {
    return description
      .replace(/hoy/g, '<span class="text-blue-600">hoy</span>')
      .replace(/mañana/g, '<span class="text-blue-600">mañana</span>');
  }

  /**
   * Obtiene el nombre de la tienda seleccionada para recoger
   * @returns String con el nombre de la tienda o null si no hay tienda seleccionada
   */
  getSelectedStore(): string | null {
    try {
      const selectedStore = this.storeService.getSelectedStore();
      if (selectedStore) {
        return selectedStore.name || null;
      }
      return null;
    } catch (error) {
      console.error('Error al obtener la tienda seleccionada:', error);
      return null;
    }
  }

  /**
   * Obtiene el texto del vehículo seleccionado
   * @returns String con el nombre del vehículo o "Vehículo seleccionado" si no hay vehículo
   */
  getVehicleText(): string {
    const selectedVehicle = this.getSelectedVehicle();
    return selectedVehicle || 'Sin vehículo';
  }

  /**
   * Obtiene el vehículo seleccionado del garage
   * @returns String con el alias del vehículo o null si no hay vehículo seleccionado
   */
  getSelectedVehicle(): string | null {
    try {
      const selectedVehicle = this.vehicleService.getDefaultVehicle();
      return selectedVehicle?.alias || null;
    } catch (error) {
      console.error('Error al obtener el vehículo seleccionado:', error);
      return null;
    }
  }

  /**
   * Carga los items guardados para más tarde desde localStorage
   */
  private loadSavedForLaterItems(): void {
    if (isPlatformBrowser(this.platformId)) {
      try {
        const saved = localStorage.getItem(LOCAL_STORAGE_SAVED_FOR_LATER);
        if (saved) {
          this.savedForLaterItems.set(JSON.parse(saved));
        }
      } catch (error) {
        console.error('Error al cargar items guardados para más tarde:', error);
      }
    }
  }

  /**
   * Guarda los items para más tarde en localStorage
   */
  private saveSavedForLaterItems(items: CartItem[]): void {
    if (isPlatformBrowser(this.platformId)) {
      try {
        localStorage.setItem(
          LOCAL_STORAGE_SAVED_FOR_LATER,
          JSON.stringify(items)
        );
        this.savedForLaterItems.set(items);
      } catch (error) {
        console.error('Error al guardar items para más tarde:', error);
      }
    }
  }

  /**
   * Mueve un item del carrito a "Guardar para más tarde"
   */
  saveForLater(item: CartItem): void {
    // Remover del carrito
    this.cartService.removeItemFromCart(item.id);

    // Agregar a "Guardar para más tarde"
    const currentSaved = this.savedForLaterItems();
    const updatedSaved = [...currentSaved, item];
    this.saveSavedForLaterItems(updatedSaved);
  }

  /**
   * Mueve un item de "Guardar para más tarde" de vuelta al carrito
   */
  moveToCart(item: CartItem): void {
    // Agregar al carrito
    this.cartService.addItemToCart({
      cantidad: item.cantidad,
      categoria: item.grupo,
      producto: item.producto,
    });

    // Remover de "Guardar para más tarde"
    const currentSaved = this.savedForLaterItems();
    const updatedSaved = currentSaved.filter((saved) => saved.id !== item.id);
    this.saveSavedForLaterItems(updatedSaved);
  }

  /**
   * Elimina un item de "Guardar para más tarde" permanentemente
   */
  removeSavedItem(itemId: number): void {
    const currentSaved = this.savedForLaterItems();
    const updatedSaved = currentSaved.filter((saved) => saved.id !== itemId);
    this.saveSavedForLaterItems(updatedSaved);
  }
}
